// Settings sync service - migrates localStorage settings to Supabase
import { supabase } from "@/integrations/supabase/client";
import { getUserSettings, UserSettings } from "./userSettings";

const SYNC_FLAG_KEY = "ayuni_settings_synced";

// Map localStorage settings to database column names
function mapSettingsToDbColumns(settings: UserSettings): Record<string, any> {
  return {
    name: settings.name || "",
    gender: settings.gender || "male",
    voice_gender: settings.voiceGender || "female",
    language: settings.language || "he",
    theme: settings.theme || "dark",
    is_muted: settings.isMuted || false,
    speech_rate: settings.speechRate || 1.0,
    control_method: settings.controlMethod || "standard_pointer",
    scan_mode: settings.scanMode || "cell_by_cell",
    scan_speed: Math.round((settings.scanSpeed || 3) * 1000), // Convert to milliseconds
    scan_type: settings.externalSwitchScanType || "automatic",
    dwell_time: 1500, // Default dwell time
    external_switch_count: settings.externalSwitchCount || 1,
    external_switch_mappings: settings.externalSwitchMappings ? Object.values(settings.externalSwitchMappings) : [],
    help_alert_type: settings.helpAlertType || "voice",
    cloned_voice_id: settings.clonedVoiceId || null,
    use_cloned_voice: settings.useClonedVoice || false,
    night_monitoring_enabled: settings.nightMonitoringEnabled || false,
    night_monitoring_start_time: settings.nightMonitoringStartTime || "22:00",
    night_monitoring_end_time: settings.nightMonitoringEndTime || "06:00",
    night_monitoring_duration: settings.nightMonitoringDuration || 5,
    caregiver_email: settings.caregiverEmail || null,
  };
}

// Check if settings have been synced for this user
function hasBeenSynced(userId: string): boolean {
  try {
    const synced = localStorage.getItem(SYNC_FLAG_KEY);
    if (!synced) return false;
    const syncedUsers: string[] = JSON.parse(synced);
    return syncedUsers.includes(userId);
  } catch {
    return false;
  }
}

// Mark settings as synced for this user
function markAsSynced(userId: string): void {
  try {
    const synced = localStorage.getItem(SYNC_FLAG_KEY);
    const syncedUsers: string[] = synced ? JSON.parse(synced) : [];
    if (!syncedUsers.includes(userId)) {
      syncedUsers.push(userId);
      localStorage.setItem(SYNC_FLAG_KEY, JSON.stringify(syncedUsers));
    }
  } catch (e) {
    console.error("[settingsSync] Failed to mark as synced:", e);
  }
}

// Sync localStorage settings to Supabase
export async function syncSettingsToSupabase(userId: string): Promise<boolean> {
  // Check if already synced
  if (hasBeenSynced(userId)) {
    console.log("[settingsSync] Settings already synced for user:", userId);
    return true;
  }

  try {
    // Get current localStorage settings
    const localSettings = getUserSettings();
    
    // Check if user has any meaningful settings (not just defaults)
    const hasCustomSettings = localSettings.name || 
      localSettings.onboardingComplete || 
      localSettings.controlMethod !== "standard_pointer";
    
    if (!hasCustomSettings) {
      console.log("[settingsSync] No custom settings to sync");
      markAsSynced(userId);
      return true;
    }

    // Map settings to database columns
    const dbSettings = mapSettingsToDbColumns(localSettings);

    // Use upsert to avoid race conditions and 409 conflicts
    const { error: upsertError } = await supabase
      .from("user_settings")
      .upsert({
        user_id: userId,
        ...dbSettings
      }, {
        onConflict: 'user_id'
      });

    if (upsertError) {
      // Handle FK constraint error (23503) - user doesn't exist in auth.users
      if (upsertError.code === '23503') {
        await supabase.auth.signOut();
      }
      return false;
    }

    markAsSynced(userId);
    console.log("[settingsSync] Successfully synced settings for user:", userId);
    return true;

  } catch (error) {
    console.error("[settingsSync] Unexpected error:", error);
    return false;
  }
}

// Load settings from Supabase and merge with localStorage
export async function loadSettingsFromSupabase(userId: string): Promise<Partial<UserSettings> | null> {
  try {
    const { data, error } = await supabase
      .from("user_settings")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("[settingsSync] Error loading settings:", error);
      return null;
    }

    if (!data) {
      return null;
    }

    // Map database columns back to UserSettings
    return {
      name: data.name || "",
      gender: (data.gender as "male" | "female" | "neutral") || "male",
      voiceGender: (data.voice_gender as "male" | "female") || "female",
      language: data.language || "he",
      theme: (data.theme as "light" | "dark" | "auto") || "dark",
      isMuted: data.is_muted || false,
      speechRate: data.speech_rate || 1.0,
      controlMethod: data.control_method || "standard_pointer",
      scanMode: (data.scan_mode as "cell_by_cell" | "row_and_column") || "cell_by_cell",
      scanSpeed: (data.scan_speed || 3000) / 1000, // Convert from milliseconds
      externalSwitchScanType: (data.scan_type as "automatic" | "manual") || "automatic",
      externalSwitchCount: data.external_switch_count || 1,
      helpAlertType: (data.help_alert_type as any) || "voice",
      clonedVoiceId: data.cloned_voice_id || null,
      useClonedVoice: data.use_cloned_voice || false,
      nightMonitoringEnabled: data.night_monitoring_enabled || false,
      nightMonitoringStartTime: data.night_monitoring_start_time || "22:00",
      nightMonitoringEndTime: data.night_monitoring_end_time || "06:00",
      nightMonitoringDuration: data.night_monitoring_duration || 5,
      caregiverEmail: data.caregiver_email || null,
    };

  } catch (error) {
    console.error("[settingsSync] Unexpected error loading settings:", error);
    return null;
  }
}
